import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Bell, Check, Inbox, Truck, Wrench, X, type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { relativeTime } from '../relativeTime';
import {
  useMarkAllRead, useMarkRead, useNotifications, useUnreadCount, type UserNotification,
} from '../hooks/useNotifications';

/**
 * The bell in the header (fleet step 3, 2026-08-30).
 *
 * Until now it was a button with an orange dot painted on it that never
 * changed and did nothing. Now it says how many things need you, opens to
 * the list, and a row takes you straight to the thing — Vic to the request
 * in his inbox, a requester to the machine's page.
 *
 * Desktop: a popover under the button. `compact` (the phone header): the
 * same panel pinned full-width under the header bar, with a Close, because
 * there is no "outside" to click on a phone. It anchors to the header (the
 * root goes `static` so the header is the containing block), never to the
 * viewport — the NADC bar pushes the whole page down on phones and a
 * viewport-fixed panel sat over it and the header. Either way Escape and an
 * outside click close it, every row is a button so the list reads and works
 * from the keyboard, and closing hands focus back to the bell when it was
 * inside the panel.
 */

const ICON_OF_KIND: Record<string, LucideIcon> = {
  'move.requested': Inbox,
  'move.approved': Check,
  'move.arrived': Check,
  'move.dates_changed': Truck,
  'move.departed': Truck,
  'move.declined': X,
  'move.claim_overridden': AlertTriangle,
  'rental.added': Wrench,
};

const TONE_OF_KIND: Record<string, string> = {
  'move.declined': 'text-danger-400',
  'move.claim_overridden': 'text-amber-400',
  'move.approved': 'text-signal-400',
  'move.arrived': 'text-signal-400',
};

export function NotificationBell({ compact = false }: { compact?: boolean }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const unreadQ = useUnreadCount();
  const unread = unreadQ.data?.meta?.unread ?? 0;
  const listQ = useNotifications(open);
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  // Focus that was inside the panel goes back to the bell, so a keyboard
  // user is not dropped at the top of the document when the panel unmounts.
  const close = useCallback(() => {
    if (panelRef.current?.contains(document.activeElement)) buttonRef.current?.focus();
    setOpen(false);
  }, []);

  // Escape and outside-click close. Mousedown rather than click so a drag
  // that starts inside and ends outside does not dismiss it.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    function onPointer(e: MouseEvent | TouchEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [open, close]);

  function openRow(row: UserNotification) {
    if (!row.read_at) markRead.mutate(row.id);
    close();
    navigate(row.link);
  }

  const rows = listQ.data?.data ?? [];

  return (
    <div ref={rootRef} className={cn('relative', compact && 'static')}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-label={`Notifications, ${unread} unread`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className={cn(
          'relative rounded-md text-graphite-400 hover:text-graphite-100 hover:bg-graphite-800 transition-colors',
          // The desk's: 36px in the 48px top row, 44 on a touch screen.
          compact ? 'flex items-center justify-center w-9 h-9' : 'flex h-9 w-9 items-center justify-center [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11',
          open && 'text-graphite-100 bg-graphite-800',
        )}
      >
        <Bell className="w-5 h-5" aria-hidden="true" />
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-safety-500 text-white text-[11px] font-bold leading-[18px] text-center tabular-nums"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label="Notifications"
          className={cn(
            // Over the Guide (z-[70]): its side panel starts under the top row
            // so the bell stays usable while it is open, and its sheet on a
            // phone covers the lower part of the screen. What the bell opens
            // must draw over either.
            'z-[75] flex flex-col overflow-hidden rounded-lg border border-graphite-700 bg-graphite-900 shadow-xl',
            compact
              ? 'absolute left-2 right-2 top-full mt-1 max-h-[75vh]'
              : 'absolute right-0 top-full mt-2 w-96 max-h-[70vh]',
          )}
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-graphite-800">
            <h2 className="text-sm font-semibold text-graphite-100">Notifications</h2>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={unread === 0 || markAllRead.isPending}
                className="px-2 py-1 rounded text-xs font-medium text-safety-500 hover:text-safety-400 hover:bg-graphite-800 disabled:text-graphite-600 disabled:hover:bg-transparent transition-colors"
              >
                Mark all read
              </button>
              {compact && (
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close"
                  className="flex items-center justify-center w-8 h-8 rounded text-graphite-400 hover:text-graphite-100 hover:bg-graphite-800"
                >
                  <X className="w-4 h-4" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          <div className="overflow-y-auto">
            {listQ.isPending ? (
              <p className="px-4 py-6 text-sm text-graphite-400" aria-live="polite">Loading…</p>
            ) : listQ.isError ? (
              <p className="px-4 py-6 text-sm text-graphite-400">Couldn&rsquo;t load notifications — try again shortly.</p>
            ) : rows.length === 0 ? (
              <p className="px-4 py-6 text-sm text-graphite-300">Nothing needs you</p>
            ) : (
              <ul className="divide-y divide-graphite-800">
                {rows.map((row) => <NotificationRow key={row.id} row={row} onOpen={openRow} />)}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationRow({ row, onOpen }: { row: UserNotification; onOpen: (row: UserNotification) => void }) {
  const Icon = ICON_OF_KIND[row.kind] ?? Bell;
  const isUnread = !row.read_at;
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(row)}
        className={cn(
          'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-graphite-800/60 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-safety-500',
          isUnread ? 'bg-graphite-800/25' : '',
        )}
      >
        <span className={cn('mt-0.5 shrink-0', TONE_OF_KIND[row.kind] ?? 'text-graphite-400')}>
          <Icon className="w-4 h-4" aria-hidden="true" />
        </span>
        <span className="min-w-0 grow">
          <span className={cn('block text-sm', isUnread ? 'font-semibold text-graphite-100' : 'text-graphite-200')}>
            {row.title}
          </span>
          {row.body && (
            <span className="mt-0.5 block text-xs text-graphite-400 line-clamp-1">{row.body}</span>
          )}
          <span className="mt-1 block text-[11px] text-graphite-500">
            <time dateTime={row.created_at}>{relativeTime(row.created_at)}</time>
          </span>
        </span>
        {isUnread && (
          <span className="mt-1.5 shrink-0 inline-block w-2 h-2 rounded-full bg-safety-500">
            <span className="sr-only">Unread</span>
          </span>
        )}
      </button>
    </li>
  );
}
